import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { newAxiosClient } from "@shadowcue/ui";
import type { IUser } from "../../../types/iuser";
import {
  PageContainer,
  PageTopbar,
  PageHeader,
  ContentContainer,
  SectionHeader,
  StatCard,
  StatGrid,
  TemplateSectionCard,
  ScoredQuestionRow,
  astryx,
  neutralTheme,
  useDocumentTheme,
  type IScriptTemplate,
  type IScriptTemplateQuestionAnalytics,
} from "@shadowcue/ui";
import {
  AVG_SCORE_TONE,
  MIN_RESPONSES_FOR_EFFECTIVENESS,
  avgScoreTier,
  effectivenessTier,
  effectivenessTone,
  flattenQuestions,
  meanEffectivenessPct,
  toPercent,
} from "./analytics_helpers";

interface ScriptTemplateShowProps {
  currentUser: IUser;
}

const axios = newAxiosClient();

const pluralize = (count: number, noun: string) =>
  `${count} ${count === 1 ? noun : `${noun}s`}`;

const formatPct = (value: number | null | undefined): string => {
  if (value == null) return "—";
  return `${Math.round(value * 100)}%`;
};

const formatDecimal = (value: number | null | undefined): string => {
  if (value == null) return "—";
  return value.toFixed(2);
};

// The 5-answer minimum shows up in several places; keep it one string so the
// cards, the popover and the thin-data note can never drift apart.
const EFFECTIVENESS_RULE =
  `Only questions with at least ${MIN_RESPONSES_FOR_EFFECTIVENESS} answers are counted.`;

interface MetricDescriptor {
  label: string;
  value: string;
  description: string;
}

// Effectiveness leads: it is the number now shown on the chip, so the popover
// opens by explaining it. The two measures it combines follow immediately.
const buildMetricDescriptors = (
  analytics: IScriptTemplateQuestionAnalytics,
  maxScore: number | null,
): MetricDescriptor[] => [
  {
    label: "Effectiveness",
    value: formatPct(analytics.effectivenessScore),
    description:
      "How well this question separates strong candidates from weak ones. It combines the two measures below. Under 30% means it isn't telling you much.",
  },
  {
    label: "Clear answers",
    value: formatPct(analytics.evaluability),
    description:
      "How often answers land clearly high or low instead of bunching in the middle. 70% means about 7 in 10 answers gave a clear read.",
  },
  {
    label: "Score spread",
    value: formatDecimal(analytics.differentiation),
    description:
      "How far apart candidate scores fall, in points. Near 0 means nearly everyone scores the same.",
  },
  {
    label: "Avg. score",
    value:
      analytics.avgScore != null && maxScore != null
        ? `${analytics.avgScore.toFixed(1)} / ${maxScore}`
        : "—",
    description: "The average score candidates get on this question.",
  },
  {
    label: "Follow-up rate",
    value: formatPct(analytics.followUpRate),
    description:
      "How often the AI had to ask a follow-up because the first answer wasn't enough.",
  },
  {
    label: "Weak question",
    value: analytics.worthReviewing ? "Yes" : "No",
    description:
      `Flagged when effectiveness is under 30%. ${EFFECTIVENESS_RULE}`,
  },
  {
    label: "Responses",
    value: `${analytics.totalResponses}`,
    description: "How many answers these numbers are based on.",
  },
];

const buildMetricsContent = (
  analytics: IScriptTemplateQuestionAnalytics,
  maxScore: number | null,
): React.ReactNode => {
  const metrics = buildMetricDescriptors(analytics, maxScore);
  // Explains why the chip is grey rather than colored.
  const isThinData =
    analytics.totalResponses < MIN_RESPONSES_FOR_EFFECTIVENESS;

  return (
    <astryx.VStack gap={3}>
      <astryx.Text type="label" weight="bold">
        Question metrics
      </astryx.Text>
      {isThinData && (
        <astryx.Text type="supporting" color="secondary">
          {`Only ${pluralize(analytics.totalResponses, "answer")} so far — these numbers need at least ${MIN_RESPONSES_FOR_EFFECTIVENESS} to be reliable.`}
        </astryx.Text>
      )}
      <astryx.Divider />
      {metrics.map((metric) => (
        <astryx.VStack gap={0.5} key={metric.label}>
          <astryx.HStack justify="between" align="center" gap={3}>
            <astryx.Text type="label">{metric.label}</astryx.Text>
            <astryx.Text type="label" hasTabularNumbers>
              {metric.value}
            </astryx.Text>
          </astryx.HStack>
          <astryx.Text type="supporting" color="secondary">
            {metric.description}
          </astryx.Text>
        </astryx.VStack>
      ))}
    </astryx.VStack>
  );
};

const ScriptTemplateShow = ({ currentUser }: ScriptTemplateShowProps) => {
  const [template, setTemplate] = useState<IScriptTemplate | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const params = useParams();
  const navigate = useNavigate();
  const mode = useDocumentTheme();

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(
          `/api/script_templates/${params.scriptTemplateId}`,
          {
            params: { include_scoring: true },
            headers: { AUTHORIZATION: `Bearer ${currentUser.authToken}` },
          },
        );
        setTemplate(res.data.scriptTemplate);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const sections = template?.scriptTemplateSections ?? [];
  const allQuestions = flattenQuestions(sections);

  const sectionsCount = sections.length;
  const questionsCount = allQuestions.length;

  // No fallback: a wrong max would render a raw score as a nonsense percentage
  // (7.0 out of an assumed 5 reads as 140%). Show an em dash instead.
  const maxScore = template?.maxScore ?? null;
  const worthReviewingCount = template?.worthReviewingCount ?? 0;

  const templateEffectivenessPct = meanEffectivenessPct(allQuestions);

  const avgScorePct =
    template?.avgScore != null && maxScore != null
      ? Math.round((template.avgScore / maxScore) * 100)
      : null;

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <astryx.LayerProvider>
        <PageContainer>
          <PageTopbar
            breadcrumbs={[
              { label: "Script templates", href: "/admin/script_templates" },
              { label: template?.name ?? "Loading…" },
            ]}
          />

          <ContentContainer loading={loading}>
            {template && (
              <>
                <PageHeader
                  title={template.name}
                  description="Read-only view with aggregate performance metrics."
                  action={
                    <astryx.Button
                      label="Edit"
                      onClick={() =>
                        navigate(
                          `/admin/script_templates/${params.scriptTemplateId}/edit`,
                        )
                      }
                    />
                  }
                />

                <astryx.VStack gap={8}>
                  <astryx.VStack gap={4}>
                    <SectionHeader
                      title="Performance"
                      description="Aggregate scoring across all sessions"
                    />
                    <StatGrid>
                      <StatCard
                        label="Effectiveness"
                        tone={effectivenessTone(templateEffectivenessPct)}
                        value={
                          templateEffectivenessPct != null
                            ? `${templateEffectivenessPct}%`
                            : "—"
                        }
                        info={`How well this template's questions separate strong candidates from weak ones. ${EFFECTIVENESS_RULE}`}
                      />
                      <StatCard
                        label="Weak Questions"
                        tone={worthReviewingCount > 0 ? "critical" : "neutral"}
                        value={worthReviewingCount}
                        info={`Questions that aren't telling you much, because candidates score about the same on them. ${EFFECTIVENESS_RULE}`}
                      />
                      <StatCard
                        label="Avg. Score"
                        tone={
                          template.avgScore != null && maxScore != null
                            ? AVG_SCORE_TONE[
                                avgScoreTier(template.avgScore, maxScore)
                              ]
                            : "neutral"
                        }
                        value={avgScorePct != null ? `${avgScorePct}%` : "—"}
                        info="The average score candidates get on this template, as a percentage of the top score."
                      />
                      <StatCard
                        label="Total Sessions"
                        tone="neutral"
                        value={template.totalSessions ?? "—"}
                        info="How many interviews have used this template."
                      />
                    </StatGrid>
                  </astryx.VStack>

                  <astryx.VStack gap={4}>
                    <SectionHeader
                      title="Interview script"
                      description="Sections and questions"
                      action={
                        <astryx.Badge
                          label={`${pluralize(sectionsCount, "section")} · ${pluralize(questionsCount, "question")}`}
                        />
                      }
                    />
                    <astryx.VStack gap={4}>
                      {sections.map((section, i) => {
                        const sectionPct = meanEffectivenessPct(
                          section.scriptTemplateQuestions,
                        );

                        return (
                          <TemplateSectionCard
                            key={section.id ?? i}
                            index={i + 1}
                            name={section.name}
                            avgScore={sectionPct}
                            maxScore={100}
                            scoreFormat="percent"
                            scoreTier={effectivenessTier(sectionPct)}
                            scoreLabel="Effectiveness"
                          >
                            <astryx.VStack gap={0}>
                              {section.scriptTemplateQuestions.map(
                                (question, j) => {
                                  const analytics = question.analytics;
                                  const questionPct = toPercent(
                                    analytics?.effectivenessScore,
                                  );

                                  return (
                                    <ScoredQuestionRow
                                      key={question.id ?? `${i}.${j}`}
                                      index={j + 1}
                                      body={question.body}
                                      score={questionPct}
                                      max={100}
                                      scoreFormat="percent"
                                      scoreTier={effectivenessTier(
                                        questionPct,
                                        analytics,
                                      )}
                                      idealResponse={
                                        question.prospectiveResponse
                                      }
                                      metrics={
                                        analytics != null &&
                                        analytics.totalResponses > 0
                                          ? buildMetricsContent(
                                              analytics,
                                              maxScore,
                                            )
                                          : undefined
                                      }
                                    />
                                  );
                                },
                              )}
                            </astryx.VStack>
                          </TemplateSectionCard>
                        );
                      })}
                    </astryx.VStack>
                  </astryx.VStack>
                </astryx.VStack>
              </>
            )}
          </ContentContainer>
        </PageContainer>
      </astryx.LayerProvider>
    </astryx.Theme>
  );
};

export default ScriptTemplateShow;
